import React, { useContext, useEffect, useMemo, useState } from 'react';
import produce from 'immer';
import {
  EditorDeclarativeBlock,
  Experience,
} from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { ContentLocation } from '@/features/editor/widgets/shared/location-picker/use-location-picker';
import { LocationPicker } from '@/features/editor/widgets/shared/location-picker';
import { AllMutationKind, MoveChange } from '@/pkg/sdk';
import { getDeviceBasedEditorId } from '@/webapi/common';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

export interface MoveElementWidgetProps {
  originalChange: EditorDeclarativeBlock;
  isNewChange?: boolean;
}

const hasMoveEditorId = (change: EditorDeclarativeBlock) =>
  change.editorId?.startsWith(`move-`);

export function MoveElementWidget({
  originalChange,
  isNewChange,
}: MoveElementWidgetProps) {
  const alignPreviewRight = useFeatureBit(FeatureBit.ALIGN_PREVIEW_RIGHT);
  const {
    cleanLastPickedElement,
    devicePreview: { editorState },
    inspectorNav: { gotoChangelog },
    experienceState: {
      currentExperience,
      currentVariantIdx,
      removeEditorChange,
      upsertEditorChange,
    },
  } = useContext(EditorContext);

  const [change] = useState(() =>
    hasMoveEditorId(originalChange)
      ? originalChange
      : produce(originalChange, (draft) => {
          draft.editorId = getDeviceBasedEditorId(
            `move-${originalChange.block.selector}`,
            editorState.device,
            currentExperience.experienceCreationVersion,
          );
        }),
  );
  const isReassignedEditorId = change !== originalChange;

  const removeMoveWithOriginalEditorId = (draft: Experience) => {
    const variant = draft.variants[currentVariantIdx];
    variant.changes = variant.changes?.filter(
      (ch) =>
        !(
          ch.editorId === originalChange.editorId &&
          ch.block?.kind === `moveElem`
        ),
    );
  };

  const saveChange = (toSave: EditorDeclarativeBlock) => {
    if (isReassignedEditorId) {
      upsertEditorChange(
        toSave,
        false,
        true,
        0,
        removeMoveWithOriginalEditorId,
      );
    } else {
      upsertEditorChange(toSave);
    }
  };

  const [location, setLocation] = useState<ContentLocation | undefined>(
    undefined,
  );

  const onLocationChanged = (selector: string, placement: AllMutationKind) => {
    setLocation({ selector, placement });
  };

  const onBack = () => {
    if (isNewChange) {
      removeEditorChange(change);
    } else {
      saveChange(change);
    }
    cleanLastPickedElement();
    gotoChangelog();
  };

  const onContinue = () => {
    setTimeout(() => {
      cleanLastPickedElement();
    }, 500);
    gotoChangelog();
  };

  const modifiedChange = useMemo(() => {
    if (location) {
      return produce(change, (draft) => {
        (draft.block.value as MoveChange).destSelector = location.selector;
        draft.block.value[`htmlKind`] = location.placement;
      });
    }
    return change;
  }, [location]);

  useEffect(() => {
    saveChange(modifiedChange);
  }, [modifiedChange]);

  return (
    <LocationPicker
      title="Choose a new position for your element"
      description={`To move the selected section, navigate to the desired location on the ${
        alignPreviewRight ? `right` : `left`
      }. Once you\`ve found the spot, click the pink plus button to place it there.`}
      onContinue={onContinue}
      onBack={onBack}
      backCaption="< Back to changes"
      onLocationChanged={onLocationChanged}
    />
  );
}
